/**
 * Connector catalog — flat list of all capability specs the integrations
 * index page renders. Keeping this list separate from `wizards/index.ts`
 * lets the route iterate without forcing every spec import into the
 * critical path.
 */

import { getEnv } from "@ando/core";
import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import {
  accountingSpec,
  arubaPecSpec,
  cadLibrarySpec,
  calendarSpec,
  cassettoFiscaleSpec,
  crmSpec,
  databaseSpec,
  driveSpec,
  emailSpec,
  erpSpec,
  filesystemSpec,
  infocertPecSpec,
  legalStorageSpec,
  messagingSpec,
  openBankingSpec,
  projectManagementSpec,
  sdiSpec,
  spreadsheetSpec,
  vatVerifierSpec,
} from "./wizards";

/**
 * Workspace area a connector feeds — mirrors the BE crosswalk (``ando-agent``
 * ``RECOMMENDABLE_CONNECTORS.area`` + ``workspace_overview._EXTRA_CONNECTOR_AREAS``).
 * Single source of truth for the scalable hub→integrations targeting: a hub
 * asks for its OWN area and ``integrationTargetForArea`` derives the
 * destination — add a connector here and every hub CTA updates with zero
 * hub edits.
 */
export type ConnectorArea =
  | "calendar"
  | "conversations"
  | "documents"
  | "finance"
  | "sales"
  | "technical"
  | "work";

export interface CatalogEntry {
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  /** Route slug — must match the file in `routes/_authed/workspace/integrations/`. */
  readonly slug: string;
  /** Sort group on the index page. */
  readonly group: "productivity" | "knowledge" | "finance" | "compliance" | "infrastructure";
  /** Workspace area this connector feeds — drives hub→integrations targeting. */
  readonly area: ConnectorArea;
  /**
   * Desktop-only: the connector requires the native Tauri filesystem edge
   * (local scan / SMB / LDAP). Hidden entirely on the web build — there is no
   * web "bridge" anymore. Filtered out of `visibleConnectorCatalog` when
   * `getEnv().isTauri` is false.
   */
  readonly desktopOnly?: boolean;
}

// Casting via `as unknown as` because each spec's `TConn` extends `BaseConnection`
// but TypeScript can't unify them in a heterogeneous array. The cast is safe:
// the catalog only invokes spec methods that accept any `BaseConnection`.
const entry = <TConn extends BaseConnection>(
  spec: CapabilityWizardSpec<TConn>,
  slug: string,
  group: CatalogEntry["group"],
  area: ConnectorArea,
  opts?: { readonly desktopOnly?: boolean },
): CatalogEntry => ({
  spec: spec as unknown as CapabilityWizardSpec<BaseConnection>,
  slug,
  group,
  area,
  desktopOnly: opts?.desktopOnly,
});

export const connectorCatalog: readonly CatalogEntry[] = [
  entry(crmSpec, "crm", "productivity", "sales"),
  entry(projectManagementSpec, "project-management", "productivity", "work"),
  entry(calendarSpec, "calendar", "productivity", "calendar"),
  entry(emailSpec, "email", "productivity", "conversations"),
  entry(messagingSpec, "messaging", "productivity", "conversations"),

  // ── Conoscenza — document / file knowledge sources (cloud + local) ──────
  // Drive (cloud), NAS/filesystem (local/desktop), spreadsheet and CAD are
  // all "feed Ando documents to read" sources. Grouped together so the user
  // sees one coherent "where my knowledge lives" section, distinct from
  // structured-data infrastructure (database/erp).
  entry(driveSpec, "drive", "knowledge", "documents"),
  entry(spreadsheetSpec, "spreadsheet", "knowledge", "documents"),
  entry(filesystemSpec, "filesystem", "knowledge", "documents", { desktopOnly: true }),
  entry(cadLibrarySpec, "cad-library", "knowledge", "technical"),

  entry(accountingSpec, "accounting", "finance", "finance"),
  entry(openBankingSpec, "open-banking", "finance", "finance"),
  entry(sdiSpec, "sdi-invoicing", "finance", "finance"),
  entry(vatVerifierSpec, "vat-verifier", "finance", "finance"),

  entry(arubaPecSpec, "aruba-pec", "compliance", "conversations"),
  entry(infocertPecSpec, "infocert-pec", "compliance", "conversations"),
  entry(cassettoFiscaleSpec, "cassetto-fiscale", "compliance", "finance"),
  entry(legalStorageSpec, "legal-storage", "compliance", "finance"),

  // ── Infrastruttura — structured-data connectors (query-time, not docs) ──
  // ``database`` (Direct DB) + ``erp`` (SAP/Zucchetti/TeamSystem). These feed
  // text-to-SQL, not the document RAG, so they stay distinct from the
  // "Conoscenza" document sources above.
  entry(databaseSpec, "database", "infrastructure", "work"),
  entry(erpSpec, "erp", "infrastructure", "work"),
];

/**
 * Catalog filtered for the CURRENT runtime: drops ``desktopOnly`` entries on
 * the web build. ``isTauri`` is stable per session (it's `"__TAURI_INTERNALS__"
 * in window`), so evaluating once at module load is correct and keeps the
 * index page's ``useQueries`` fan-out index-aligned with its summaries.
 *
 * The integrations index + ``$capability`` detail route iterate THIS list, not
 * ``connectorCatalog``, so a web user never sees — nor can navigate to — the
 * filesystem connector.
 */
export const visibleConnectorCatalog: readonly CatalogEntry[] = connectorCatalog.filter(
  (e) => !e.desktopOnly || getEnv().isTauri,
);

/**
 * Canonical TanStack Query key for ``listCapabilityConnections`` calls.
 * Three call sites used to inline ``["connectors","list",slug]`` —
 * extracted here so the prefix is single-source: any future change
 * (e.g. tenant scoping) ripples through.
 *
 *   connectorsListKey()        → ["connectors","list"]            // prefix
 *   connectorsListKey("crm")   → ["connectors","list","crm"]      // per-cap
 */
export function connectorsListKey(
  capability?: string,
): readonly ["connectors", "list", string] | readonly ["connectors", "list"] {
  return capability
    ? (["connectors", "list", capability] as const)
    : (["connectors", "list"] as const);
}

/**
 * Enterprise (structured-data) capabilities → the BE ``connector_type``
 * values they cover. These connectors live on a SEPARATE surface
 * (``/api/v1/enterprise/connectors``, persisted to ``enterprise_connections``)
 * and are NOT in the white-label ``SUPPORTED_CAPABILITIES`` whitelist, so
 * the unified ``/connectors/{capability}/connections`` route 400s on them.
 * The connection dispatch (``listConnectionsForCapability``) reads this map
 * to route these three to the enterprise endpoint + filter the flat list.
 *
 * Values mirror ``ConnectorType`` in
 * ``ando-agent/src/services/enterprise_connectors/base.py``.
 */
export const ENTERPRISE_CONNECTOR_TYPES: Readonly<Record<string, readonly string[]>> = {
  // Each capability lists the BE ``connector_type`` values it covers
  // (must mirror ``ando-agent/src/services/enterprise_connectors/base.py::ConnectorType``).
  // The FE's ``isEnterpriseCapability`` / ``useStructuredDataConnectors``
  // derive routing + the "Focus · Structured" picker list from this
  // single map — adding a capability here is enough to surface it in
  // the composer scope picker, no extra wiring required.
  database: ["postgresql", "mysql", "sqlserver", "oracle", "sqlite", "generic_odbc"],
  erp: ["sap_odata", "zucchetti", "teamsystem", "sap_b1"],
  spreadsheet: ["spreadsheet"],
  // Open Banking (PSD2 / AISP) — ``enterprise_connectors.list``
  // rewrites the internal provider ``"banking"`` to the public
  // connector_type ``"openbanking"`` (see
  // ``ando-agent/src/api/v1/endpoints/enterprise_connectors.py:1492``).
  open_banking: ["openbanking"],
  // Jun 4 2026 parity pass — V1 surfaced CRM / accounting / CAD /
  // SDI invoicing in the structured picker too. V2 was missing these
  // because the map only listed DB + ERP + spreadsheet. Wired now so
  // queries with ``focus="structured"`` can target these sources via
  // the same ``enterprise_connection_ids[]`` payload.
  crm: ["salesforce", "hubspot"],
  accounting: ["fatture_in_cloud"],
  cad_library: ["autocad"],
  sdi_invoicing: ["aruba_sdi"],
};

/** ``true`` when a capability is served by the enterprise-connector surface. */
export function isEnterpriseCapability(capability: string): boolean {
  return capability in ENTERPRISE_CONNECTOR_TYPES;
}

/**
 * Capabilities whose connect flow bypasses the unified
 * ``POST /connectors/{capability}/start`` router and goes straight to
 * ``POST /api/v1/enterprise/connectors`` (host/port/user/password, no
 * OAuth round-trip). These are intentionally absent from the BE
 * ``SUPPORTED_CAPABILITIES`` whitelist (see
 * ``ando-agent/src/api/v1/endpoints/connectors_unified.py``), so hitting
 * ``/start`` for them yields ``Unknown capability: <slug>``.
 *
 * Narrower than ``ENTERPRISE_CONNECTOR_TYPES`` on purpose — that map
 * also lists OAuth-based capabilities (``crm``, ``accounting``,
 * ``open_banking``, …) added later for the structured-data picker, but
 * those still go through ``/start`` to launch their OAuth handshake.
 *
 * Spreadsheet is conceptually a member but uses its own
 * ``SpreadsheetConnectDialog`` and never reaches the shared
 * ``handleStartFlow`` path, so it stays out of this list.
 */
const LOCAL_CREDENTIALS_CAPABILITIES: ReadonlySet<string> = new Set(["database", "erp"]);

export function isLocalCredentialsCapability(capability: string): boolean {
  return LOCAL_CREDENTIALS_CAPABILITIES.has(capability);
}

export function groupLabel(g: CatalogEntry["group"]): string {
  switch (g) {
    case "productivity":
      return i18nSingleton._(msg`Produttività`);
    case "knowledge":
      return i18nSingleton._(msg`Conoscenza`);
    case "finance":
      // SMB-italian lessico (UI/UX audit 2026-05-30): mirrors the
      // ``Amministrazione`` top-tab — banca + fatture + fisco + CRM in
      // a single SMB-friendly umbrella.
      return i18nSingleton._(msg`Amministrazione`);
    case "compliance":
      return i18nSingleton._(msg`Compliance italiana`);
    case "infrastructure":
      return i18nSingleton._(msg`Infrastruttura`);
  }
}

/**
 * Canonical group display order — May 24 2026.
 *
 * Iterating ``Object.entries(groupedDisconnected)`` used insertion order,
 * which depended on whichever group's first row happened to arrive
 * first in the filtered list. Result: "Finance" would jump above
 * "Produttività" on a tenant who already connected Gmail+Drive but
 * not their CRM. Pinning the order here makes the page layout
 * deterministic regardless of connection state.
 */
export const GROUP_ORDER: ReadonlyArray<CatalogEntry["group"]> = [
  "productivity",
  "knowledge",
  "finance",
  "compliance",
  "infrastructure",
];

/**
 * Brand accent tokens — drives the colored ``border-l`` and chip badge
 * on the index page section headers AND the gated/connected chips on
 * row cards. Uses the Ando palette (no raw hex) so dark-mode + theme
 * refactors stay one-line.
 *
 * ``connected`` is a semantic tone (not a catalog group) for the
 * "Collegate" section and the "Da connettere" / "Piano superiore"
 * row chips. Reading from this map means we never re-hardcode
 * ``bg-success/15`` etc. in JSX.
 */
export type AccentTone = CatalogEntry["group"] | "connected";

export const GROUP_ACCENT: Readonly<
  Record<AccentTone, { border: string; chipBg: string; chipText: string }>
> = {
  productivity: {
    border: "border-ando-orange-soft",
    chipBg: "bg-ando-orange-soft",
    chipText: "text-ando-orange",
  },
  knowledge: {
    border: "border-amber-500/40",
    chipBg: "bg-amber-500/15",
    chipText: "text-amber-700",
  },
  finance: {
    border: "border-success/40",
    chipBg: "bg-success/15",
    chipText: "text-success",
  },
  compliance: {
    border: "border-warning/40",
    chipBg: "bg-warning/15",
    chipText: "text-warning",
  },
  infrastructure: {
    border: "border-ink-3/30",
    chipBg: "bg-ink-3/15",
    chipText: "text-ink-2",
  },
  connected: {
    border: "border-success/50",
    chipBg: "bg-success/15",
    chipText: "text-success",
  },
};
